import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Table, Button, Space, Tag, Modal, Form, InputNumber, Input, Statistic, Row, Col, Select } from 'antd';
import { PlayCircleOutlined, PauseCircleOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  useOperationTimeLogsByStatus,
  useStartOperation,
  useCompleteOperation,
  usePauseOperation,
  useResumeOperation,
} from '@/hooks/useManufacturingPhase2';
import { OperationTimeLog, OperationStatus } from '@/services/manufacturing-phase2.service';

const OperationTimeTrackingPage: React.FC = () => {
  const [form] = Form.useForm();
  const [completeModalVisible, setCompleteModalVisible] = useState(false);
  const [selectedLog, setSelectedLog] = useState<OperationTimeLog | null>(null);
  const [filterStatus, setFilterStatus] = useState<OperationStatus>(OperationStatus.IN_PROGRESS);

  // API hooks
  const { data: timeLogs = [], isLoading } = useOperationTimeLogsByStatus(filterStatus);
  const startOperation = useStartOperation();
  const completeOperation = useCompleteOperation();
  const pauseOperation = usePauseOperation();
  const resumeOperation = useResumeOperation();

  const handleStart = (id: string) => {
    startOperation.mutate(id);
  };

  const handlePause = (id: string) => {
    pauseOperation.mutate(id);
  };

  const handleResume = (id: string) => {
    resumeOperation.mutate(id);
  };

  const handleComplete = (record: OperationTimeLog) => {
    setSelectedLog(record);
    form.setFieldsValue({
      quantity_completed: 0,
      quantity_rejected: 0,
      quantity_rework: 0,
    });
    setCompleteModalVisible(true);
  };

  const handleSubmitComplete = async () => {
    try {
      const values = await form.validateFields();
      await completeOperation.mutateAsync({
        id: selectedLog!.id,
        data: values,
      });
      setCompleteModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Form validation error:', error);
    }
  };

  const getStatusTag = (status: OperationStatus) => {
    const statusConfig = {
      [OperationStatus.PENDING]: { color: 'default', icon: <ClockCircleOutlined /> },
      [OperationStatus.IN_PROGRESS]: { color: 'processing', icon: <PlayCircleOutlined /> },
      [OperationStatus.COMPLETED]: { color: 'success', icon: <CheckCircleOutlined /> },
      [OperationStatus.PAUSED]: { color: 'warning', icon: <PauseCircleOutlined /> },
      [OperationStatus.CANCELLED]: { color: 'error', icon: null },
    };
    const config = statusConfig[status];
    return <Tag color={config.color} icon={config.icon}>{status}</Tag>;
  };

  const columns: ColumnsType<OperationTimeLog> = [
    {
      title: 'Work Order',
      dataIndex: 'work_order_id',
      key: 'work_order_id',
      width: 150,
      render: (_id: string, record: OperationTimeLog) => {
        const label =
          record.work_order_number?.trim() ||
          (record.work_order_id ? `WO-${record.work_order_id.substring(0, 8)}` : '—');
        return record.work_order_id ? (
          <Link to={`/mfg/work-orders/${record.work_order_id}`}>{label}</Link>
        ) : (
          label
        );
      },
    },
    {
      title: 'Operation',
      dataIndex: ['operation', 'operation_name'],
      key: 'operation',
      width: 200,
      render: (name) => name || 'Unknown',
    },
    {
      title: 'Workstation',
      dataIndex: ['workstation', 'workstation_name'],
      key: 'workstation',
      width: 150,
      render: (name) => name || 'Unknown',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Start Time',
      dataIndex: 'actual_start_time',
      key: 'actual_start_time',
      width: 180,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: 'Duration (hrs)',
      key: 'duration',
      width: 120,
      align: 'right',
      render: (_, record) => {
        if (!record.actual_start_time) return '-';
        const end = record.actual_end_time ? new Date(record.actual_end_time) : new Date();
        const start = new Date(record.actual_start_time);
        const hours = (end.getTime() - start.getTime()) / 3600000;
        return hours.toFixed(2);
      },
    },
    {
      title: 'Efficiency',
      dataIndex: 'efficiency_percentage',
      key: 'efficiency_percentage',
      width: 110,
      align: 'right',
      render: (eff) => {
        if (!eff) return '-';
        const color = eff >= 90 ? 'success' : eff >= 70 ? 'warning' : 'error';
        return <Tag color={color}>{eff.toFixed(1)}%</Tag>;
      },
    },
    {
      title: 'Qty Completed',
      dataIndex: 'quantity_completed',
      key: 'quantity_completed',
      width: 120,
      align: 'right',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => {
        if (record.status === OperationStatus.PENDING) {
          return (
            <Button
              type="primary"
              size="small"
              icon={<PlayCircleOutlined />}
              onClick={() => handleStart(record.id)}
            >
              Start
            </Button>
          );
        }
        if (record.status === OperationStatus.IN_PROGRESS) {
          return (
            <Space size="small">
              <Button
                size="small"
                icon={<PauseCircleOutlined />}
                onClick={() => handlePause(record.id)}
              >
                Pause
              </Button>
              <Button
                type="primary"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => handleComplete(record)}
              >
                Complete
              </Button>
            </Space>
          );
        }
        if (record.status === OperationStatus.PAUSED) {
          return (
            <Button
              type="primary"
              size="small"
              icon={<PlayCircleOutlined />}
              onClick={() => handleResume(record.id)}
            >
              Resume
            </Button>
          );
        }
        return <Tag color="success">Completed</Tag>;
      },
    },
  ];

  // Calculate summary statistics
  const summary = {
    total: timeLogs.length,
    pending: timeLogs.filter((log: OperationTimeLog) => log.status === OperationStatus.PENDING).length,
    in_progress: timeLogs.filter((log: OperationTimeLog) => log.status === OperationStatus.IN_PROGRESS).length,
    completed: timeLogs.filter((log: OperationTimeLog) => log.status === OperationStatus.COMPLETED).length,
    avg_efficiency: timeLogs.filter((log: OperationTimeLog) => log.efficiency_percentage).length > 0
      ? (timeLogs.reduce((sum: number, log: OperationTimeLog) => sum + (log.efficiency_percentage || 0), 0) /
        timeLogs.filter((log: OperationTimeLog) => log.efficiency_percentage).length).toFixed(1)
      : 0,
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Statistic title="Total Operations" value={summary.total} />
          </Col>
          <Col span={6}>
            <Statistic
              title="In Progress"
              value={summary.in_progress}
              valueStyle={{ color: '#145A54' }}
              prefix={<PlayCircleOutlined />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Completed Today"
              value={summary.completed}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Avg Efficiency"
              value={summary.avg_efficiency}
              suffix="%"
              valueStyle={{ color: Number(summary.avg_efficiency) >= 85 ? '#52c41a' : '#faad14' }}
            />
          </Col>
        </Row>
      </Card>

      <Card
        title="Operation Time Tracking"
        extra={
          <Space>
            <span>Filter by Status:</span>
            <Select
              value={filterStatus}
              onChange={setFilterStatus}
              style={{ width: 150 }}
            >
              {Object.values(OperationStatus).map(status => (
                <Select.Option key={status} value={status}>{status}</Select.Option>
              ))}
            </Select>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={timeLogs}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 1500 }}
          pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
        />
      </Card>

      <Modal
        title="Complete Operation"
        open={completeModalVisible}
        onCancel={() => setCompleteModalVisible(false)}
        onOk={handleSubmitComplete}
        confirmLoading={completeOperation.isPending}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="quantity_completed"
            label="Quantity Completed"
            rules={[{ required: true, message: 'Please enter quantity completed' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="quantity_rejected" label="Quantity Rejected" initialValue={0}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="quantity_rework" label="Quantity Rework" initialValue={0}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="actual_setup_time_hours" label="Actual Setup Time (hours)">
            <InputNumber min={0} step={0.25} precision={2} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="actual_run_time_hours" label="Actual Run Time (hours)">
            <InputNumber min={0} step={0.1} precision={2} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="actual_teardown_time_hours" label="Actual Teardown Time (hours)">
            <InputNumber min={0} step={0.25} precision={2} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} placeholder="Any notes or observations..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OperationTimeTrackingPage;

